import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { DOCUMENT_LOAD_STATUS } from "../../constants/metaData";
import { getDocumentDetails } from "../../api/documentsAPI";
import UserAuth from "../../../auth/userAuth";
import { serializeError } from "../../utils/serializeError";
import { normalizeDocumentRevisionInfoResponse } from "../../../utils/normalizeDocumentRevisionInfoResponse";

const userAuth = new UserAuth();

export const fetchDocumentDetails = createAsyncThunk(
  "documents/documentDetails/fetchDocumentDetails",
  async (file, { rejectWithValue }) => {
    try {
      const tokenResponse = await userAuth.getUserToken(true);
      const accessToken = tokenResponse?.accessToken ?? null;
      if (!accessToken) {
        return rejectWithValue({ message: "No access token" });
      }
      const response = await getDocumentDetails(file, accessToken);
      return response;
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);

const initialState = {
  isOpen: false,
  status: DOCUMENT_LOAD_STATUS.onStart,
  data: {},
  error: null,
  dialogTitle: "",
  dialogVariant: "info",
  showHeader: true,
  showSpinner: false,
  showDetailsContent: false,
  emptyStateType: null, // "noData" | "error" | null
};

const documentDetailsSlice = createSlice({
  name: "documents/documentDetails",
  initialState,
  reducers: {
    closeDocumentDetails: (state) => {
      state.isOpen = false;
    },
    openDocumentDetails: (state) => {
      state.isOpen = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocumentDetails.pending, (state) => {
        state.isOpen = true;
        state.status = DOCUMENT_LOAD_STATUS.onLoading;
        state.data = {};
        state.error = null;
        state.dialogTitle = "Please wait";
        state.dialogVariant = "info";
        state.showHeader = false;
        state.showSpinner = true;
        state.showDetailsContent = false;
        state.emptyStateType = null;
      })
      .addCase(fetchDocumentDetails.fulfilled, (state, action) => {
        const payload = action.payload ?? {};
        const { revision } = normalizeDocumentRevisionInfoResponse(payload.data);
        const hasRevision =
          revision &&
          typeof revision === "object" &&
          Object.keys(revision).length > 0;
        state.isOpen = true;
        if (payload.status === 200 && hasRevision) {
          state.status = DOCUMENT_LOAD_STATUS.onCompletion;
          state.data = revision;
          state.error = null;
          state.dialogTitle = "Details from IFS Cloud";
          state.dialogVariant = "info";
          state.showHeader = false;
          state.showSpinner = false;
          state.showDetailsContent = true;
          state.emptyStateType = null;
        } else if (payload.status === 204 || (payload.status === 200 && !hasRevision)) {
          state.status = DOCUMENT_LOAD_STATUS.noData;
          state.data = {};
          state.error = null;
          state.dialogTitle = "No details found";
          state.dialogVariant = "warning";
          state.showHeader = false;
          state.showSpinner = false;
          state.showDetailsContent = false;
          state.emptyStateType = "noData";
        } else {
          state.status = DOCUMENT_LOAD_STATUS.onError;
          state.data = {};
          state.error = { message: `Status ${payload.status}` };
          state.dialogTitle = "Unable to load details";
          state.dialogVariant = "error";
          state.showHeader = false;
          state.showSpinner = false;
          state.showDetailsContent = false;
          state.emptyStateType = "error";
        }
      })
      .addCase(fetchDocumentDetails.rejected, (state, action) => {
        state.isOpen = true;
        state.status = DOCUMENT_LOAD_STATUS.onError;
        state.data = {};
        state.error = action.payload ?? serializeError(action.error);
        state.dialogTitle = "Unable to load details";
        state.dialogVariant = "error";
        state.showHeader = false;
        state.showSpinner = false;
        state.showDetailsContent = false;
        state.emptyStateType = "error";
      });
  },
});

export const { closeDocumentDetails, openDocumentDetails } =
  documentDetailsSlice.actions;
export default documentDetailsSlice.reducer;

